// React-Bridge des Strangler-Programms (Plan sharded-hatching-clover, Phase 5;
// Runbook: tools/REACT-STRANGLER.md).
//
// Ein React-Screen ist fuer den Legacy-Kern eine GANZ NORMALE Komponente:
// er haengt im Komponentenbaum (POS.getComponentById laeuft ueber
// children[]), wird per Main.addChild(CFoo) erzeugt, per draw()/removeMe()/
// reDraw()/lock()/setTransmitted() behandelt - und sein Wurzel-Element baut
// weiterhin der CComponentBuilder (ID, name, class, Einhaengen in den Eltern-
// knoten, transmitted-Flag: byte-identisch zu vorher). React rendert nur das
// INNERE des Wurzel-Elements, synchron (flushSync), damit Aufrufer wie
// `cmp.draw(); document.getElementById(cmp.id)...` weiter funktionieren.
//
// Events gehen NICHT an React vorbei, sondern durch den Legacy-Dispatcher:
// jedes interaktive Element meldet sich ueber bridge.widget() mit einem
// Schatten-Handle im children[]-Baum an. POS.events.eventTrigger(element,
// "onMousedown") findet damit per ID eine Komponente mit properties.events
// und faehrt die unveraenderte Kette (Dispatcher-Lock, Parameter aus den
// on*_parameter_N-Attributen, CComponent.eventTrigger -> Widget-Hook ->
// POS.timeout.setTimeout -> Handler(component, parameters) -> unlock).
// Verhaltensgleichheit entsteht so durch Konstruktion, nicht durch Nachbau.
//
// Regeln fuer React-Screens (Pixel-/DOM-Paritaet, siehe Runbook):
//  - JSX reproduziert das Legacy-DOM exakt: Verschachtelung, IDs als
//    `${bridge.id}:n`, name="<LegacyKlasse>:<id>", class-Strings inkl. der
//    Builder-Eigenheiten ("CCaption undefined", "CCaption  "), Inline-Styles
//    in derselben Schluessel-Reihenfolge, innerHTML-Werte per
//    dangerouslySetInnerHTML (der Builder setzte innerHTML).
//  - Attribut-REIHENFOLGE darf abweichen (React setzt Props in Prop-Reihen-
//    folge, on*_parameter_N-Attribute kommen per ref dazu, weil React alle
//    "on*"-Props verschluckt) - das DOM-Oracle vergleicht seit 21.8.2026
//    attributreihenfolge-unabhaengig (tests/visual.js canonicalDom).
//
// ECHTE LEGACY-KIND-WIDGETS (spec.mountLegacy, seit 21.8.2026): Screens mit
// gewachsenen Widgets (CPagedPushPanel, CNumPad, CMultiClickDate) baut man
// NICHT in JSX nach - das wuerde Widget-Logik duplizieren. Stattdessen laeuft
// nach dem React-Mount der Haken spec.mountLegacy(bridge): dort erzeugt der
// Screen die Widgets wie im Legacy (bridge.createChildrenFromXML(...) bzw.
// bridge.addChild(...)) und zeichnet sie. Warum das ohne Konflikt geht:
//  - React rendert die Kinder 0..n-1; jedes meldet ueber bridge.el/widget ein
//    Schatten-Handle an, danach steht childrenCount auf n. Das Legacy-Widget
//    bekommt damit automatisch den naechsten Index (und die richtige ID).
//  - CComponentBuilder.doAddElement haengt sein Element per appendChild an
//    das mainElement des Eltern-Components, also an die Bridge-WURZEL - es
//    landet damit HINTER allen React-Knoten. Genau dort steht es im Legacy
//    auch, weil es zuletzt gezeichnet wird. React fasst diesen Knoten nie an:
//    er gehoert zu keinem Fiber, und React fuegt eigene Kinder nur relativ zu
//    seinen EIGENEN Knoten ein. Ein Legacy-Widget MITTEN zwischen React-
//    Kindern waere dagegen nicht sicher - dann JSX-Reihenfolge anpassen.
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { CPushButton } from '../components/pushbutton.js';
import { CComponent } from '../components/component.js';

// Legacy-Eventname (Builder: "on" + Typ mit grossem Anfangsbuchstaben, Rest
// klein: onMousedown) -> React-Prop (onMouseDown). Unbekannte Namen werden
// durchgereicht (React ignoriert sie dann - besser auffaellig als falsch).
const REACT_EVENT = {
  onMousedown: 'onMouseDown', onMouseup: 'onMouseUp', onMouseout: 'onMouseOut',
  onMouseover: 'onMouseOver', onMousemove: 'onMouseMove', onClick: 'onClick',
  onDblClick: 'onDoubleClick', onKeydown: 'onKeyDown', onKeyup: 'onKeyUp',
  onKeypress: 'onKeyPress', onFocus: 'onFocus', onBlur: 'onBlur',
  onChange: 'onChange', onSubmit: 'onSubmit', onSelect: 'onSelect',
  onLoad: 'onLoad', onError: 'onError',
};

export class CReactBridge extends CComponent {
  // spec: { addclass, Screen, htmlType?, legacyName?, prepare?(bridge),
  //         initialState?, methods?: { name(bridge, ...args) } }
  //  - addclass:   Zusatzklassen des Wurzel-Elements, exakt wie der Legacy-
  //                Konstruktor sie an construct() gab ("CAboutMode CashWindow")
  //  - legacyName: this.name der Legacy-Klasse. Viele Screens setzen es NIE
  //                und behalten "CComponent" - dann weglassen.
  //  - Screen:     React-Komponente, bekommt { bridge } als Props.
  //  - prepare:    Pendant zu Legacy-prepare() OHNE createChildrenFromXML
  //                (z.B. Main.components.payment.setEnabled(false)). Laeuft einmal vor dem
  //                ersten Rendern, wie bisher aus draw() heraus.
  constructor(id, addclass, spec) {
    super();
    this.construct(id, spec.addclass);
    this.name = spec.legacyName || 'CComponent';
    this.htmlType = spec.htmlType || 'div';
    // spec.rootStyles = die Styles, die der Legacy-KONSTRUKTOR gesetzt hat.
    // Sie gehoeren hierher und nicht in prepare(): Aufrufer duerfen zwischen
    // Konstruktion und draw() weitere Styles anhaengen (CPOS.inputPOSID setzt
    // zIndex), und die Reihenfolge im style-Attribut haengt daran.
    this.properties = { styles: spec.rootStyles ? { ...spec.rootStyles } : {} };
    this.spec = spec;
    this.Main = null;
    // spec.fields(): die Instanz-Felder des Legacy-KONSTRUKTORS. Sie muessen
    // hier gesetzt werden und nicht in prepare(): prepare laeuft erst bei
    // draw(), und Aufrufer belegen zwischen Konstruktion und draw() gerne
    // schon Felder (CMainPOS setzt mode/showPaydown und ruft sogar
    // buildArrayBons, CMainPOS.drawSubmenus ruft setMain). Als Funktion,
    // damit jede Instanz eigene Arrays/Objekte bekommt.
    if (typeof spec.fields == 'function') Object.assign(this, spec.fields());
    this.reactRoot = null;
    // Screen-Zustand fuer zustandsbehaftete Screens (Legacy: reValue/reStyle
    // auf this.components.X). Legacy-Aufrufer rufen weiter cmp.show(...) o.ae.
    // - solche Methoden kommen aus spec.methods und aendern den Zustand ueber
    // bridge.update(patch), was synchron neu rendert.
    this.state = spec.initialState ? { ...spec.initialState } : {};
    if (spec.methods)
      for (const m in spec.methods) this[m] = (...args) => spec.methods[m](this, ...args);
    // spec.legacyMethods: Prototype-Methoden, die WOERTLICH aus dem
    // Legacy-Screen uebernommen sind. Sie werden unveraendert an die Instanz
    // gehaengt, `this` ist damit die Bridge - Signaturen wie
    // (component, parameters) und Muster wie POS.getComponentById(
    // parameters[0]) stimmen also weiter. Fuer die grossen Widget-Huellen
    // (CPosBalancing: 56 Methoden, 1400 Zeilen Geldlogik) ist das der
    // sichere Weg: kein Abtippen, keine Signatur-Uebersetzung, kein Risiko
    // einer stillen Abweichung. spec.methods bleibt fuer Screens, deren
    // Methoden auf bridge.state arbeiten.
    if (spec.legacyMethods)
      for (const m in spec.legacyMethods) this[m] = spec.legacyMethods[m];
    // i18n wie bisher: gleiche Pipe-Dateien, identische Strings.
    this.t = (section, key) => POS.lang.getText(section, key);
  }

  // --- Legacy-Lebenszyklus --------------------------------------------------

  // spec.prepare darf ein jQuery-Deferred zurueckgeben (Screens, die in
  // prepare() SQL fahren - CCancelBonMode). Gibt es nichts zurueck, bleibt
  // der ganze Ablauf SYNCHRON wie bisher: $.when(null) loest sofort auf, und
  // Aufrufer wie COtherFunctions.aboutWindow finden ihr DOM direkt nach
  // draw() vor. Rueckgabe ist immer ein jQuery-Deferred (Hauptrunbook
  // Regel 4), nie ein natives Promise.
  draw(transmit) {
    const def = $.Deferred();
    const weiter = () => {
      if (this.collector != null) this.builder.collector = this.collector;
      // Wurzel-Element durch den Builder - identisch zu CComponent.draw():
      // addElement(htmlType, properties) haengt es in den Eltern-mainElement
      // (oder in ein transmitDocument, wenn es eine Wurzel ist) und setzt
      // transmitted.
      this.create();
      this.mount();
      $.when(typeof this.spec.mountLegacy == 'function' ? this.spec.mountLegacy(this) : null)
        .done(() => {
          if (typeof transmit == 'undefined' || transmit == true) this.transmit();
          def.resolve();
        });
    };
    if (!this.prepared) {
      $.when(typeof this.spec.prepare == 'function' ? this.spec.prepare(this) : null)
        .done(() => { this.prepared = true; weiter(); });
    } else {
      weiter();
    }
    return def.promise();
  }

  mount() {
    this.reactRoot = createRoot(this.builder.mainElement);
    this.render();
  }

  render() {
    if (!this.reactRoot) return;
    // synchron: der Aufrufer erwartet das fertige DOM direkt nach draw()
    flushSync(() => {
      this.reactRoot.render(createElement(this.spec.Screen, { bridge: this }));
    });
  }

  update(patch) {
    Object.assign(this.state, patch);
    this.render();
  }

  unmount() {
    if (!this.reactRoot) return;
    this.reactRoot.unmount();
    this.reactRoot = null;
  }

  removeMe() {
    if (this.transmitted == false) return;
    this.unmount();
    // entfernt das Wurzel-Element aus dem DOM + setTransmitted(false)
    super.removeMe();
  }

  // Legacy-reDraw baut neu und haengt das Element ans Ende des Elternknotens
  // (removeMe + transmit). Hier dasselbe Endergebnis: abbauen, neu zeichnen.
  reDraw() {
    const def = $.Deferred();
    if (this.transmitted) {
      this.unmount();
      CComponent.prototype.removeMe.call(this);
    }
    this.prepared = true;
    $.when(this.draw()).done(() => def.resolve());
    return def.promise();
  }

  // --- Hilfen fuer den React-Screen -----------------------------------------

  childId(path) { return this.id + ':' + path; }

  // Das Schatten-Handle an einem Pfad (nach dem Mount). Damit verdrahten
  // Screens in spec.mountLegacy die Verweise, die im Legacy der XML-Builder
  // gesetzt hat (labeled.field.input, numPad.controlFields).
  handleAt(path) {
    let knoten = this;
    for (const teil of String(path).split(':')) {
      if (!knoten) return null;
      knoten = knoten.children[Number(teil)];
    }
    return knoten;
  }

  // Schatten-Handle fuer Pfad "n" oder "n:m:..." (relativ zur Wurzel) im
  // Komponentenbaum anlegen/ersetzen. JEDES gerenderte Legacy-Element bekommt
  // eines (auch passive), damit children[] wie im Legacy-Baum dicht ist:
  // lock()/setTransmitted()/setMovers() iterieren ueber childrenCount, und
  // POS.getComponentById(<irgendeine DOM-ID>) loest wie bisher auf.
  //  - properties: WEITERE Legacy-Properties des Widgets (keyValue eines
  //    NumPad-Knopfes, type/maxLength/OKcallback eines CInput ...). Sie
  //    werden nur bei der ERZEUGUNG gesetzt, nie bei einem Neu-Rendern -
  //    sonst wuerde React den getippten Wert eines Eingabefeldes wieder
  //    ueberschreiben.
  _handle(path, legacyName, { events = {}, attributes = {}, properties = null, proto = null } = {}) {
    const parts = String(path).split(':').map(Number);
    let parent = this;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!parent.children[parts[i]]) this._handle(parts.slice(0, i + 1).join(':'), 'CComponent');
      parent = parent.children[parts[i]];
    }
    const index = parts[parts.length - 1];
    const prototyp = proto || CComponent.prototype;
    let handle = parent.children[index];

    // Steht an dieser Stelle schon dasselbe Widget, wird es WIEDERVERWENDET.
    // Das ist kein Sparen, sondern noetig: gewachsene Widgets fuehren
    // Zustand in properties (CInput den getippten Wert, CNumPad seine
    // controlFields), und fremder Code haelt Referenzen darauf. Ein
    // Neu-Rendern darf beides nicht wegwerfen.
    if (!handle || handle.name !== legacyName || Object.getPrototypeOf(handle) !== prototyp) {
      handle = Object.create(prototyp);
      handle.id = this.childId(path);
      handle.name = legacyName;
      handle.children = (parent.children[index] && parent.children[index].children) || [];
      handle.childrenCount = handle.children.length;
      handle.transmitted = true;
      handle.prepared = true;
      handle.locked = false;
      handle.mainLocked = false;
      handle.enabledLock = false;
      handle.properties = { styles: {} };
      if (properties) Object.assign(handle.properties, properties);
      // Minimaler Builder-Ersatz. Legacy-Kinder haengen sich beim Zeichnen
      // ueber `POS.getComponentById(elternId).builder.mainElement` ein
      // (CComponentBuilder.doAddElement) - ohne das kann ein React-Knoten
      // kein Legacy-Widget aufnehmen. Das Element wird ABSICHTLICH erst bei
      // Bedarf gesucht: beim Anlegen des Handles steht es noch nicht im DOM.
      // Gebraucht von CPosReceiptList, dessen Bonrenderer je Bonzeile ein
      // CPre in eine Gitterzelle zeichnet.
      // Sicherheitsnetz (30.9.2026): ruft Legacy-Code auf einem Handle
      // draw()/reDraw() auf, landet er in CComponent.create/transmit und
      // damit hier. Ohne diese drei warf das "this.builder.addElement is not
      // a function" - ein ungefangener Fehler startet die Kasse neu zur
      // Anmeldung (Wertkarten-Scan auf der Testkasse). Stattdessen: React
      // neu rendern und im Log WARNEN, damit die Stelle gefunden und
      // richtig (ueber bridge.update) umgestellt wird.
      const bridge = this;
      const legacyZeichnen = function (art) {
        try {
          log.add(1, 'Warning', 'React-Handle', `Legacy-${art} auf ${handle.id} (${legacyName}) - neu gerendert statt gebaut`);
        } catch (e) { /* log fehlt nur in Einzeltests */ }
        if (bridge.reactRoot) bridge.render();
      };
      handle.builder = {
        id: handle.id,
        collector: false,
        get mainElement() { return document.getElementById(handle.id); },
        addElement() { legacyZeichnen('addElement'); },
        build() {},
        doAddElement() { legacyZeichnen('doAddElement'); },
      };
      // Dasselbe fuer reDraw: CComponent.reDraw (die EINZIGE reDraw-
      // Implementierung im Legacy) zeichnet ueber den Builder und ruft danach
      // removeMe() - auf einem Handle loeschte das den von React verwalteten
      // Knoten (Zeitliste verschwand, Code-Suche 30.9.2026). Standard jetzt:
      // neu rendern. Widgets mit eigenem, besserem Ersatz ueberschreiben ihn;
      // das Kennzeichen bridgeStandard erlaubt ihnen, ihn zu erkennen.
      const standardReDraw = function () {
        legacyZeichnen('reDraw');
        return jQuery.Deferred().resolve().promise();
      };
      standardReDraw.bridgeStandard = true;
      handle.reDraw = standardReDraw;
      parent.children[index] = handle;
    }
    // Was die JSX bei JEDEM Rendern erklaert, gewinnt; was fremder Code
    // sonst an das Handle gehaengt hat, bleibt stehen.
    handle.properties.events = events;
    handle.properties.attributes = Object.assign(handle.properties.attributes || {}, attributes);
    if (parent.childrenCount <= index) parent.childrenCount = index + 1;
    return handle;
  }

  // Ein Handle SCHON VOR dem ersten Rendern anlegen. Gebraucht von Screens,
  // deren prepare() bereits auf Kinder zugreift: CPosReceiptList gibt dem
  // CBonManager im Konstruktor sein Bon-Gitter mit, CTimeRecord fuellt seine
  // Liste noch in prepare. Beim Rendern wird dasselbe Handle
  // wiederverwendet (gleicher Name, gleicher Prototyp) - deshalb MUSS der
  // Name hier schon der endgueltige sein, sonst legt das Rendern ein zweites
  // an und die Verweise zeigen ins Leere.
  // Anders als _handle wirken die uebergebenen properties hier AUCH auf ein
  // schon bestehendes Handle. Grund (24.8.2026, beim CMainPOS-Port gefunden):
  // _handle weist properties nur im Erzeugungszweig zu. Das erste Rendern legt
  // das Handle an, mountLegacy ruft danach gitterVorbereiten/preVorbereiten -
  // und deren Eigenschaften verpufften still. Konkret blieb canSelect am
  // Bon-Gitter false, womit den Bonzeilen der Klick-Handler und ihre
  // Parameter-Attribute fehlten: der Bon liess sich nicht mehr anwaehlen.
  // Nur FEHLENDE Schluessel werden gefuellt, damit ein spaeterer Aufruf
  // nichts ueberschreibt, was fremder Code inzwischen gesetzt hat.
  handleVorbereiten(path, legacyName, spec = {}) {
    const handle = this._handle(path, legacyName, spec);
    if (spec.properties)
      for (const s in spec.properties)
        if (!(s in handle.properties)) handle.properties[s] = spec.properties[s];
    return handle;
  }

  // Handle an einem festen Pfad OHNE DOM-Anteil. Fuer dynamisch erzeugte
  // Kinder (Scheintasten, OK-Tasten), die das Legacy per addChild() anlegte:
  // dort vergab addChild den naechsten Kind-Index, und ob daraus ein Element
  // wurde, entschied erst ein spaeteres draw(). Genau das bilden wir nach -
  // Handle immer, Element nur wenn die JSX es rendert. Wichtig, weil die
  // Legacy-Methoden auch auf NICHT gezeichnete Kinder schreiben
  // (noteButtons[0].properties.styles.visibility) und weil ein ausgelassener
  // Index die ganze Kindfolge verschoebe.
  widgetHandle(path, legacyName = 'CPushButton', proto = null) {
    const handle = this._handle(path, legacyName, { proto: proto || CPushButton.prototype });
    // Die Vorgaben des Widgets NICHT nachbauen, sondern vom ECHTEN Konstruktor
    // holen: er setzt page/xPos/yPos/events/attributes und ruft
    // setStyleFromXML(), das etc/defaults/LYT_<Name>.xml einmischt - daher
    // stammen properties.caption und die Grundstile. Ohne das stirbt jede
    // make*Buttons-Methode an "properties.caption.value" (25.8.2026 beim
    // Umbau von CTransactionShowMode: Fehlerdialog schon im Login, weil der
    // Screen als PRELOAD jeder Sitzung gebaut wird).
    const muster = new CPushButton(handle.id);
    const bisher = handle.properties || {};
    muster.properties.styles = Object.assign(muster.properties.styles || {}, bisher.styles || {});
    handle.properties = muster.properties;
    return handle;
  }

  // Legacy-NAMENSREGISTER nachbilden. Der Builder haengt jedes benannte Kind
  // aus der Layout-XML zusaetzlich als parent.components[name] ein, und
  // FREMDER Legacy-Code adressiert Screens genau darueber - z.B. die
  // Rechteverwaltung:
  //   main.cmpCashbox.components.menuContainer.components.functionButtons
  //       .children[0].setEnabled(false)     (pos/userrights.js)
  // Ein React-Screen, der nur children[] fuehrt, laesst diesen Code mit
  // "Cannot read properties of undefined (reading 'components')" sterben -
  // live passiert beim Entschalen der Panels, mit Alert-Dialog schon im
  // Login. Jeder entschalte Screen muss seine benannten Knoten deshalb hier
  // anmelden, und zwar bei JEDEM Rendern (die Handles werden neu erzeugt).
  nameComponent(path, name) {
    const parts = String(path).split(':').map(Number);
    let parent = this;
    for (let i = 0; i < parts.length - 1; i++) parent = parent.children[parts[i]];
    const handle = parent.children[parts[parts.length - 1]];
    if (!parent.components) parent.components = {};
    parent.components[name] = handle;
    return handle;
  }

  // Adress-Props eines passiven Legacy-Elements (kein Event): id + name,
  // exakt wie der Builder sie setzt. path: "n" oder "n:m".
  // spec (optional) wie bei widget(), ohne Events: proto/properties fuer
  // passive Widgets, die trotzdem Legacy-Methoden mitbringen (CCaption
  // bringt reValue mit, CContainerTable seine Zeilen-Zugriffe).
  el(path, legacyName, spec = {}) {
    const handle = this._handle(path, legacyName, spec);
    return { id: handle.id, name: legacyName + ':' + handle.id };
  }

  // Adress-Props eines INTERAKTIVEN Legacy-Elements + Schatten-Handle mit
  // Events. events: wie Legacy-properties.events (Funktion = Handler, true =
  // nur Listener, der am Ende POS.events.unlock() ausloest - so haben Legacy-
  // Buttons ihren onMouseup/onMouseout). attributes: die on*_parameter_N-
  // Attribute (kommen per ref ins DOM, React verschluckt "on*"-Props).
  // proto: Prototype des Legacy-Widgets, damit dessen Event-Hooks greifen
  // (CPushButton.prototype: Beep bei properties.sound).
  widget(path, legacyName, spec = {}) {
    const handle = this._handle(path, legacyName, spec);
    const props = { id: handle.id, name: legacyName + ':' + handle.id };
    const events = spec.events || {};
    for (const ev in events) {
      if (!events[ev]) continue;
      props[REACT_EVENT[ev] || ev] = (e) => POS.events.eventTrigger(e.currentTarget, ev, e.nativeEvent);
    }
    const attributes = spec.attributes || {};
    props.ref = (el) => {
      if (!el) return;
      for (const a in attributes) el.setAttribute(a, attributes[a]);
    };
    return props;
  }
}

// Erzeugt den Konstruktor, den der Legacy-Kern wie jede andere Screen-Klasse
// benutzt (Main.addChild(CFoo) -> new CFoo(id, addclass)). Wird ueber die
// legacy-bridge auf window publiziert, solange Legacy-Code ihn referenziert.
export function defineReactScreen(spec) {
  const Ctor = class extends CReactBridge {
    constructor(id, addclass) { super(id, addclass, spec); }
  };
  Object.defineProperty(Ctor, 'name', { value: spec.className || 'CReactScreen' });
  return Ctor;
}
